import React, { useState } from 'react';
import { Typography, Card, Row, Col, Button, Table, Tag, Space, Tabs, Spin, Alert, Divider, Modal, Form, InputNumber, Switch, Input, Statistic } from 'antd';
import { ReloadOutlined, PlayCircleOutlined, PauseCircleOutlined, SettingOutlined, EyeOutlined, BlockOutlined } from '@ant-design/icons';
import { useTranslationWithNamespace } from '../hooks/useTranslationWithNamespace';
import { showSuccessNotification, showErrorNotification } from '../utils/notificationUtils';
import {
  useIndexerStatus,
  useIndexerConfig,
  useStartAllIndexers,
  useStopAllIndexers,
  useStartIndexer,
  useStopIndexer,
  useUpdateIndexerConfig,
  useDepositStatsByBlockchain
} from '../hooks/useIndexerManagement';
import { IndexerStatus, IndexerConfig } from '../types/indexer';
import DepositMetricsPanel from '../components/indexer/DepositMetricsPanel';
import RecentDepositsPanel from '../components/indexer/RecentDepositsPanel';
import IndexerPerformancePanel from '../components/indexer/IndexerPerformancePanel';
import IndexerMetricsPanel from '../components/indexer/IndexerMetricsPanel';
import IndexerAddressManagement from '../components/indexer/IndexerAddressManagement';
import MonitoredAddressesModal from '../components/indexer/MonitoredAddressesModal';
import HealthStatusTag from '../components/indexer/HealthStatusTag';
import HealthStatusDetailModal from '../components/indexer/HealthStatusDetailModal';
import ReprocessBlockModal from '../components/indexer/ReprocessBlockModal';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

/**
 * Deduplicate indexer status data by blockchain name (case insensitive)
 * This prevents duplicate entries for the same blockchain from appearing in the UI
 * and merges data from multiple entries for the same blockchain
 *
 * @param data Array of indexer status data
 * @returns Deduplicated array of indexer status data
 */
const deduplicateIndexerStatusData = (data: IndexerStatus[]): IndexerStatus[] => {
  if (!data || !Array.isArray(data)) return [];

  // Group by blockchain name (case insensitive)
  const blockchainGroups: Record<string, IndexerStatus[]> = {};

  data.forEach(item => {
    if (!item.blockchain) return;

    const blockchain = item.blockchain.toUpperCase();
    if (!blockchainGroups[blockchain]) {
      blockchainGroups[blockchain] = [];
    }
    blockchainGroups[blockchain].push(item);
  });

  // For each group, merge data from all entries
  return Object.entries(blockchainGroups).map(([blockchain, group]) => {
    // Start with the first item as the base
    const mergedItem = { ...group[0] };

    // Ensure required fields exist with default values
    mergedItem.lastProcessedBlock = mergedItem.lastProcessedBlock || 0;
    mergedItem.pendingBlocks = mergedItem.pendingBlocks || 0;
    mergedItem.monitoredAddresses = mergedItem.monitoredAddresses || 0;

    // If there are multiple entries, merge data from all entries
    if (group.length > 1) {
      // For each additional entry, update fields if they have values
      group.slice(1).forEach(item => {
        // Use non-zero values from additional entries if the base has zeros
        if (item.lastProcessedBlock && (!mergedItem.lastProcessedBlock || mergedItem.lastProcessedBlock === 0)) {
          mergedItem.lastProcessedBlock = item.lastProcessedBlock;
        }

        if (item.pendingBlocks && (!mergedItem.pendingBlocks || mergedItem.pendingBlocks === 0)) {
          mergedItem.pendingBlocks = item.pendingBlocks;
        }

        if (item.monitoredAddresses && (!mergedItem.monitoredAddresses || mergedItem.monitoredAddresses === 0)) {
          mergedItem.monitoredAddresses = item.monitoredAddresses;
        }

        // For other fields, use the maximum value
        mergedItem.lastBlock = Math.max(mergedItem.lastBlock || 0, item.lastBlock || 0);
        mergedItem.pendingDeposits = Math.max(mergedItem.pendingDeposits || 0, item.pendingDeposits || 0);
        mergedItem.confirmedDeposits = Math.max(mergedItem.confirmedDeposits || 0, item.confirmedDeposits || 0);
        mergedItem.failedDeposits = Math.max(mergedItem.failedDeposits || 0, item.failedDeposits || 0);
      });
    }

    return mergedItem;
  });
};

/**
 * Deduplicate indexer config data by blockchain name (case insensitive)
 * This prevents duplicate entries for the same blockchain from appearing in the UI
 *
 * @param data Array of indexer config data
 * @returns Deduplicated array of indexer config data
 */
const deduplicateIndexerConfigData = (data: IndexerConfig[]): IndexerConfig[] => {
  if (!data || !Array.isArray(data)) return [];

  // Group by blockchain name (case insensitive)
  const blockchainGroups: Record<string, IndexerConfig[]> = {};

  data.forEach(item => {
    if (!item.blockchain) return;

    const blockchain = item.blockchain.toUpperCase();
    if (!blockchainGroups[blockchain]) {
      blockchainGroups[blockchain] = [];
    }
    blockchainGroups[blockchain].push(item);
  });

  // For each group, keep only the first item
  return Object.values(blockchainGroups).map(group => group[0]);
};

const IndexerManagementPage: React.FC = () => {
  const { t: commonT } = useTranslationWithNamespace('common');
  const { t: indexerT } = useTranslationWithNamespace('indexer');

  // State
  const [activeTab, setActiveTab] = useState<string>('status');
  const [configModalVisible, setConfigModalVisible] = useState<boolean>(false);
  const [selectedIndexer, setSelectedIndexer] = useState<IndexerConfig | null>(null);
  const [forceRefresh, setForceRefresh] = useState<boolean>(false);
  const [configForm] = Form.useForm();

  // State for monitored addresses modal
  const [addressesModalVisible, setAddressesModalVisible] = useState<boolean>(false);
  const [selectedBlockchain, setSelectedBlockchain] = useState<string>('');

  // State for health status detail modal
  const [healthModalVisible, setHealthModalVisible] = useState<boolean>(false);
  const [healthBlockchain, setHealthBlockchain] = useState<string>('');

  // State for reprocess block modal
  const [reprocessBlockModalVisible, setReprocessBlockModalVisible] = useState<boolean>(false);
  const [reprocessBlockchain, setReprocessBlockchain] = useState<string>('');

  // Fetch data from API
  // Note: forceRefresh parameter is kept for backward compatibility but the API now always bypasses cache
  const { data: indexerStatus, isLoading: isStatusLoading, isError: isStatusError, refetch: refetchStatus } = useIndexerStatus(30000);
  const { data: indexerConfig, isLoading: isConfigLoading, isError: isConfigError, refetch: refetchConfig } = useIndexerConfig();
  const { data: depositStats, isLoading: isStatsLoading } = useDepositStatsByBlockchain();

  // Mutations
  const startAllIndexersMutation = useStartAllIndexers();
  const stopAllIndexersMutation = useStopAllIndexers();
  const startIndexerMutation = useStartIndexer();
  const stopIndexerMutation = useStopIndexer();
  const updateIndexerConfigMutation = useUpdateIndexerConfig();

  // Handle start all indexers
  const handleStartAllIndexers = async () => {
    try {
      setForceRefresh(true);
      await startAllIndexersMutation.mutateAsync();
      showSuccessNotification(indexerT('startAllSuccess', 'All indexers started successfully'));
      await refetchStatus();
      setForceRefresh(false);
    } catch (error) {
      setForceRefresh(false);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      showErrorNotification(indexerT('startAllError', 'Failed to start all indexers'), errorMessage);
    }
  };

  // Handle stop all indexers
  const handleStopAllIndexers = async () => {
    try {
      setForceRefresh(true);
      await stopAllIndexersMutation.mutateAsync();
      showSuccessNotification(indexerT('stopAllSuccess', 'All indexers stopped successfully'));
      await refetchStatus();
      setForceRefresh(false);
    } catch (error) {
      setForceRefresh(false);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      showErrorNotification(indexerT('stopAllError', 'Failed to stop all indexers'), errorMessage);
    }
  };

  // Handle start indexer
  const handleStartIndexer = async (blockchain: string) => {
    try {
      await startIndexerMutation.mutateAsync(blockchain);
      showSuccessNotification(indexerT('startSuccess', 'Indexer started successfully'));
      // No need to manually refetch or set forceRefresh as the mutation will handle this
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      showErrorNotification(indexerT('startError', 'Failed to start indexer'), errorMessage);
    }
  };

  // Handle stop indexer
  const handleStopIndexer = async (blockchain: string) => {
    try {
      await stopIndexerMutation.mutateAsync(blockchain);
      showSuccessNotification(indexerT('stopSuccess', 'Indexer stopped successfully'));
      // No need to manually refetch or set forceRefresh as the mutation will handle this
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      showErrorNotification(indexerT('stopError', 'Failed to stop indexer'), errorMessage);
    }
  };

  // Open configuration modal
  const openConfigModal = (indexer: IndexerConfig) => {
    setSelectedIndexer(indexer);
    configForm.setFieldsValue({
      enabled: indexer.enabled,
      confirmations: indexer.confirmations,
      webhookUrl: indexer.webhookUrl,
      useQueue: indexer.useQueue,
      pollingInterval: indexer.pollingInterval
    });
    setConfigModalVisible(true);
  };

  // Handle configuration update
  const handleUpdateConfig = async (values: any) => {
    if (!selectedIndexer) return;

    try {
      await updateIndexerConfigMutation.mutateAsync({
        _id: selectedIndexer._id,
        ...values
      });
      showSuccessNotification(indexerT('updateConfigSuccess', 'Configuration updated successfully'));
      setConfigModalVisible(false);
      refetchConfig();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      showErrorNotification(indexerT('updateConfigError', 'Failed to update configuration'), errorMessage);
    }
  };

  // Fetch performance metrics (used in the IndexerPerformancePanel component)

  // Status table columns
  const statusColumns = [
    {
      title: indexerT('blockchain'),
      dataIndex: 'blockchain',
      key: 'blockchain',
      render: (text: string) => <Text strong>{text.toUpperCase()}</Text>
    },
    {
      title: indexerT('status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        // Use the actual status values directly
        return (
          <Tag color={status === 'running' ? 'green' : status === 'stopped' ? 'blue' : 'red'}>
            {status === 'running' ? 'Running' : status === 'stopped' ? 'Stopped' : 'Error'}
          </Tag>
        );
      }
    },
    {
      title: indexerT('health', 'Health'),
      key: 'health',
      render: (_: any, record: IndexerStatus) => {
        // Create clickable health status tag
        const handleClick = () => {
          setHealthBlockchain(record.blockchain);
          setHealthModalVisible(true);
        };

        // Determine health status based on record status and healthStatus
        let healthStatus: 'healthy' | 'degraded' | 'unhealthy' | 'stopped' | 'unknown' | 'error' = 'unknown';
        let successRate: number | undefined = undefined;

        // If indexer is stopped, show stopped health status
        if (record.status === 'stopped') {
          healthStatus = 'stopped';
        }
        // If indexer has an error, show error health status
        else if (record.status === 'error') {
          healthStatus = 'error';
        }
        // If health status is available, use it
        else if (record.healthStatus) {
          // Ensure the status is one of the allowed values
          const status = record.healthStatus.status || 'unknown';
          if (status === 'healthy' || status === 'degraded' || status === 'unhealthy' ||
              status === 'stopped' || status === 'unknown' || status === 'error') {
            healthStatus = status;
          }
          successRate = record.healthStatus.successRate;
        }

        return (
          <Button type="link" onClick={handleClick} style={{ padding: 0 }}>
            <HealthStatusTag
              status={healthStatus}
              successRate={successRate}
              showDetails={!!successRate}
            />
          </Button>
        );
      }
    },
    {
      title: indexerT('lastBlock'),
      dataIndex: 'lastBlock',
      key: 'lastBlock'
    },
    {
      title: indexerT('lastProcessedBlock'),
      dataIndex: 'lastProcessedBlock',
      key: 'lastProcessedBlock'
    },
    {
      title: indexerT('pendingBlocks'),
      dataIndex: 'pendingBlocks',
      key: 'pendingBlocks'
    },
    {
      title: indexerT('monitoredAddresses'),
      dataIndex: 'monitoredAddresses',
      key: 'monitoredAddresses',
      render: (count: number, record: IndexerStatus) => (
        <Button
          type="link"
          onClick={() => {
            setSelectedBlockchain(record.blockchain);
            setAddressesModalVisible(true);
          }}
          icon={<EyeOutlined />}
        >
          {count}
        </Button>
      )
    },
    {
      title: indexerT('processingRate'),
      key: 'processingRate',
      render: (_: any, record: IndexerStatus) => (
        <span>{record.metrics?.processingRate || 0} blocks/s</span>
      )
    },
    {
      title: indexerT('blocksProcessed'),
      key: 'blocksProcessed',
      render: (_: any, record: IndexerStatus) => (
        <span>{record.metrics?.blocksProcessed || 0}</span>
      )
    },
    {
      title: indexerT('transactionsProcessed'),
      key: 'transactionsProcessed',
      render: (_: any, record: IndexerStatus) => (
        <span>{record.metrics?.transactionsProcessed || 0}</span>
      )
    },
    {
      title: indexerT('actions'),
      key: 'actions',
      render: (_: any, record: IndexerStatus) => {
        // Check if this blockchain is currently being mutated
        const isStarting = startIndexerMutation.isPending &&
                          startIndexerMutation.variables === record.blockchain;
        const isStopping = stopIndexerMutation.isPending &&
                          stopIndexerMutation.variables === record.blockchain;

        return (
          <Space>
            {record.status === 'running' ? (
              <Button
                icon={<PauseCircleOutlined />}
                onClick={() => handleStopIndexer(record.blockchain)}
                size="small"
                danger
                loading={isStopping}
                disabled={isStarting || isStopping}
                title={indexerT('stop')}
              />
            ) : (
              <Button
                icon={<PlayCircleOutlined />}
                onClick={() => handleStartIndexer(record.blockchain)}
                size="small"
                type="primary"
                loading={isStarting}
                disabled={isStarting || isStopping}
                title={indexerT('start')}
              />
            )}
            <Button
              icon={<SettingOutlined />}
              onClick={() => {
                const config = indexerConfig?.data?.find((c: IndexerConfig) => c.blockchain === record.blockchain);
                if (config) {
                  openConfigModal(config);
                }
              }}
              size="small"
              disabled={isStarting || isStopping}
              title={indexerT('configure')}
            />
            <Button
              icon={<BlockOutlined />}
              onClick={() => {
                setReprocessBlockchain(record.blockchain);
                setReprocessBlockModalVisible(true);
              }}
              size="small"
              disabled={record.status !== 'running'}
              title={indexerT('reprocessBlock')}
            />
          </Space>
        );
      }
    }
  ];

  // Config table columns
  const configColumns = [
    {
      title: indexerT('blockchain'),
      dataIndex: 'blockchain',
      key: 'blockchain',
      render: (text: string) => <Text strong>{text.toUpperCase()}</Text>
    },
    {
      title: indexerT('enabled'),
      dataIndex: 'enabled',
      key: 'enabled',
      render: (enabled: boolean) => (
        <Tag color={enabled ? 'green' : 'red'}>
          {enabled ? indexerT('yes') : indexerT('no')}
        </Tag>
      )
    },
    {
      title: indexerT('confirmations'),
      dataIndex: 'confirmations',
      key: 'confirmations'
    },
    {
      title: indexerT('useQueue'),
      dataIndex: 'useQueue',
      key: 'useQueue',
      render: (useQueue: boolean) => (
        <Tag color={useQueue ? 'blue' : 'orange'}>
          {useQueue ? indexerT('yes') : indexerT('no')}
        </Tag>
      )
    },
    {
      title: indexerT('pollingInterval'),
      dataIndex: 'pollingInterval',
      key: 'pollingInterval',
      render: (interval: number) => `${interval / 1000} ${indexerT('seconds')}`
    },
    {
      title: indexerT('actions'),
      key: 'actions',
      render: (_: any, record: IndexerConfig) => (
        <Button
          icon={<SettingOutlined />}
          onClick={() => openConfigModal(record)}
          size="small"
          title={indexerT('configure')}
        />
      )
    }
  ];

  // Stats table columns
  const statsColumns = [
    {
      title: indexerT('blockchain'),
      dataIndex: 'blockchain',
      key: 'blockchain',
      render: (text: string) => <Text strong>{text.toUpperCase()}</Text>
    },
    {
      title: indexerT('deposits'),
      dataIndex: 'deposits',
      key: 'deposits'
    },
    {
      title: indexerT('amount'),
      dataIndex: 'amount',
      key: 'amount'
    },
    {
      title: indexerT('avgAmount'),
      dataIndex: 'avgAmount',
      key: 'avgAmount'
    },
    {
      title: indexerT('successRate'),
      dataIndex: 'successRate',
      key: 'successRate',
      render: (rate: number) => `${rate.toFixed(2)}%`
    },
    {
      title: indexerT('avgConfirmationTime'),
      dataIndex: 'avgConfirmationTime',
      key: 'avgConfirmationTime',
      render: (time: number) => `${(time / 60).toFixed(2)} ${indexerT('minutes')}`
    }
  ];

  return (
    <div>
      <Row gutter={[16, 16]} align="middle">
        <Col flex="auto">
          <Title level={2}>{indexerT('title')}</Title>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                // Set loading state
                setForceRefresh(true);

                // Refetch both status and config
                // Note: The API now always bypasses cache regardless of forceRefresh parameter
                Promise.all([
                  refetchStatus(),
                  refetchConfig()
                ]).finally(() => {
                  // Reset loading state when both are done
                  setForceRefresh(false);
                });
              }}
              loading={isStatusLoading || isConfigLoading || forceRefresh}
              disabled={isStatusLoading || isConfigLoading || forceRefresh}
            >
              {commonT('actions.refresh')}
            </Button>
            <Button
              icon={<PlayCircleOutlined />}
              onClick={handleStartAllIndexers}
              type="primary"
              loading={startAllIndexersMutation.isPending}
            >
              {indexerT('startAll')}
            </Button>
            <Button
              icon={<PauseCircleOutlined />}
              onClick={handleStopAllIndexers}
              danger
              loading={stopAllIndexersMutation.isPending}
            >
              {indexerT('stopAll')}
            </Button>
          </Space>
        </Col>
      </Row>

      {(isStatusError || isConfigError) && (
        <Alert
          message={indexerT('error')}
          description={indexerT('fetchError')}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Process Information Card */}
      {indexerStatus?.processInfo && (
        <Card title={indexerT('processInfo', 'Indexer Process Information')} style={{ marginTop: 16 }}>
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <Statistic
                title={indexerT('processId', 'Process ID')}
                value={indexerStatus.processInfo.pid}
                valueStyle={{ color: '#3f8600' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title={indexerT('uptime', 'Uptime')}
                value={Math.floor(indexerStatus.processInfo.uptime / 60)}
                suffix={indexerT('minutes', 'minutes')}
                valueStyle={{ color: '#3f8600' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title={indexerT('memory', 'Memory Usage')}
                value={indexerStatus.processInfo.memoryUsage.rss}
                valueStyle={{ color: '#3f8600' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title={indexerT('startTime', 'Start Time')}
                value={new Date(indexerStatus.processInfo.startTime).toLocaleString()}
                valueStyle={{ color: '#3f8600' }}
              />
            </Col>
          </Row>
        </Card>
      )}

      {/* Process Status Alert */}
      {indexerStatus?.processStatus === 'not_running' && (
        <Alert
          message={indexerT('processNotRunning', 'Indexer Process Not Running')}
          description={indexerT('processNotRunningDesc', 'The indexer process is not running. Database operations will work, but indexers will not actually monitor blockchains until the process is started.')}
          type="warning"
          showIcon
          style={{ marginTop: 16, marginBottom: 16 }}
        />
      )}

      {indexerStatus?.processStatus === 'error' && (
        <Alert
          message={indexerT('processError', 'Indexer Process Error')}
          description={`${indexerT('processErrorDesc', 'There was an error communicating with the indexer process')}: ${indexerStatus.processError}`}
          type="error"
          showIcon
          style={{ marginTop: 16, marginBottom: 16 }}
        />
      )}

      <Card style={{ marginTop: 16 }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab={indexerT('statusTab')} key="status">
            {isStatusLoading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin size="large" />
              </div>
            ) : (
              <Table
                columns={statusColumns}
                dataSource={indexerStatus?.data ? deduplicateIndexerStatusData(indexerStatus.data) : []}
                rowKey="blockchain"
                pagination={false}
              />
            )}
          </TabPane>
          <TabPane tab={indexerT('configTab')} key="config">
            {isConfigLoading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin size="large" />
              </div>
            ) : (
              <Table
                columns={configColumns}
                dataSource={indexerConfig?.data ? deduplicateIndexerConfigData(indexerConfig.data) : []}
                rowKey="_id"
                pagination={false}
              />
            )}
          </TabPane>
          <TabPane tab={indexerT('statsTab')} key="stats">
            {isStatsLoading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin size="large" />
              </div>
            ) : (
              <Table
                columns={statsColumns}
                dataSource={Array.isArray(depositStats) ? depositStats : []}
                rowKey="blockchain"
                pagination={false}
              />
            )}
          </TabPane>
        </Tabs>
      </Card>

      <Divider />

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <IndexerMetricsPanel />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}>
          <IndexerPerformancePanel />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}>
          <DepositMetricsPanel />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}>
          <RecentDepositsPanel />
        </Col>
      </Row>

      <Divider />

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}>
          <Title level={3}>{indexerT('addressManagement')}</Title>
        </Col>
      </Row>

      {/* Address Management Panels for each blockchain */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        {indexerStatus?.data && Array.isArray(indexerStatus.data) &&
          deduplicateIndexerStatusData(indexerStatus.data)
            .filter(item => item.status === 'running')
            .map(item => (
              <Col span={12} key={`address-management-${item.blockchain}`}>
                <IndexerAddressManagement blockchain={item.blockchain} />
              </Col>
            ))
        }
      </Row>

      {/* Configuration Modal */}
      <Modal
        title={indexerT('configureIndexer', { blockchain: selectedIndexer?.blockchain.toUpperCase() })}
        open={configModalVisible}
        onCancel={() => setConfigModalVisible(false)}
        footer={null}
      >
        <Form
          form={configForm}
          layout="vertical"
          onFinish={handleUpdateConfig}
        >
          <Form.Item
            name="enabled"
            label={indexerT('enabled')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="confirmations"
            label={indexerT('confirmations')}
            rules={[{ required: true, message: indexerT('confirmationsRequired') }]}
          >
            <InputNumber min={1} max={100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="webhookUrl"
            label={indexerT('webhookUrl')}
            tooltip={indexerT('webhookUrlTooltip', 'Optional. If not provided, the system default webhook URL will be used.')}
            rules={[{ required: false }]}
          >
            <Input placeholder={indexerT('webhookUrlPlaceholder', 'Leave empty to use system default')} />
          </Form.Item>
          <Form.Item
            name="useQueue"
            label={indexerT('useQueue')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="pollingInterval"
            label={indexerT('pollingInterval')}
            rules={[{ required: true, message: indexerT('pollingIntervalRequired') }]}
          >
            <InputNumber min={1000} max={300000} step={1000} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item>
            <Row justify="end" gutter={8}>
              <Col>
                <Button onClick={() => setConfigModalVisible(false)}>
                  {commonT('actions.cancel')}
                </Button>
              </Col>
              <Col>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={updateIndexerConfigMutation.isPending}
                >
                  {commonT('actions.save')}
                </Button>
              </Col>
            </Row>
          </Form.Item>
        </Form>
      </Modal>

      {/* Monitored Addresses Modal */}
      <MonitoredAddressesModal
        blockchain={selectedBlockchain}
        visible={addressesModalVisible}
        onClose={() => setAddressesModalVisible(false)}
      />

      {/* Health Status Detail Modal */}
      <HealthStatusDetailModal
        blockchain={healthBlockchain}
        visible={healthModalVisible}
        onClose={() => setHealthModalVisible(false)}
      />

      {/* Reprocess Block Modal */}
      <ReprocessBlockModal
        blockchain={reprocessBlockchain}
        visible={reprocessBlockModalVisible}
        onClose={() => setReprocessBlockModalVisible(false)}
      />
    </div>
  );
};

export default IndexerManagementPage;
